import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import LaddaButton, { SLIDE_DOWN, SLIDE_LEFT } from "react-ladda";
import { showToast } from "../../../../utils/Helper";
import CommentForm from "./CommentForm";
import CircularProgress from "../../../CircularProgress/CircularProgress";

// Ported from Summary.js — same visibility logic and handlers, rendered inline
// below the changes display. Kept self-contained: fetches allowedActions and
// the UpdateKYCStatus parameter on mount, replicates closeAlert/alertToROS/
// closeAccount handlers.

const sectionCardStyle = {
  width: "100%",
  background: "#ffffff",
  border: "1px solid #e3e8ec",
  borderRadius: "6px",
  padding: "16px 18px",
};

const sectionTitleStyle = {
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: "#455a64",
  marginBottom: "12px",
};

const actionsRowStyle = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  alignItems: "center",
};

const BTN_CLASS = "btn btn-ladda btn-primary btn-sm";
const BTN_COLOR = "primary";
const BTN_SIZE = "sm";

// Natural-language toast and confirmation copy keyed per action.
const MESSAGES = {
  closeAlert: {
    success: "Change request was successfully closed.",
    error: "Could not close change request. Please try again.",
    confirm: "Are you sure you want to close this change request?",
  },
  markAsROS: {
    success: "Change request was marked as ROS.",
    error: "Could not mark change request as ROS. Please try again.",
    confirm: "Are you sure you want to mark this change request as ROS?",
  },
  kycNotApproved: {
    success: "Change request was marked as not approved.",
    error: "Could not mark change request as not approved. Please try again.",
    confirm: "Are you sure you want to mark this KYC change request as not approved?",
  },
  sendToAdvisor: {
    success: "Change request was successfully sent to advisor.",
    error: "Could not send change request to advisor. Please try again.",
    confirm: "Are you sure you want to send this change request to the advisor?",
  },
  sendToCompliance: {
    success: "Change request was successfully sent to compliance.",
    error: "Could not send change request to compliance. Please try again.",
    confirm: "Are you sure you want to send this change request to compliance?",
  },
  secondApproval: {
    success: "Change request was successfully sent for second approval.",
    error: "Could not send change request for second approval. Please try again.",
    confirm: "Are you sure you want to send this change request for second approval?",
  },
  approve: {
    success: "Change request was successfully approved.",
    error: "Could not approve change request. Please try again.",
    confirm: "Are you sure you want to approve this change request?",
  },
  reject: {
    success: "Change request was successfully rejected.",
    error: "Could not reject change request. Please try again.",
    confirm: "Are you sure you want to reject this change request?",
  },
  closeAccount: {
    success: "Account was successfully closed.",
    error: "Could not close account. Please try again.",
    confirm: "Are you sure you want to close this account?",
  },
  sendToStep: {
    success: "Change request was successfully sent to the selected step.",
    error: "Could not send change request to the selected step. Please try again.",
    confirm: "Are you sure you want to send this change request to the selected step?",
  },
};

export default function ChangesActions({ alert, alertEventId, role, fetchs, currentUser, onResolved }) {
  const alertId = (alert && alert.id) || alertEventId;
  const status = alert && alert.status;
  const processes = alert && alert.processes;
  const requestClosure = alert && alert.requestClosure;
  const closedAccount = alert && alert.closedAccount;
  const closedAccountDate = alert && alert.closedAccountDate;
  const name = alert && alert.name;
  const typeAlias = alert && alert.typeAlias;

  const [closing, setClosing] = useState(0);
  const [sending, setSending] = useState(0);
  const [allowedActions, setAllowedActions] = useState(null);
  // Track when the async fetches that decide which buttons to show have settled,
  // so we can render a spinner first instead of flashing the wrong button set
  // (e.g. legacy "Close alert" before enabledAlertFeatures/allowedActions load).
  const [allowedLoaded, setAllowedLoaded] = useState(false);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);
  const [updateKYCStatus, setUpdateKYCStatus] = useState(false);
  const [loadingCloseAccount, setLoadingCloseAccount] = useState(false);
  const [showMessageCloseAccount, setShowMessageCloseAccount] = useState(false);
  const [enabledAlertFeatures, setEnabledAlertFeatures] = useState(null);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [availableSteps, setAvailableSteps] = useState([]);
  const [selectedStep, setSelectedStep] = useState(null);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [sendingStep, setSendingStep] = useState(false);
  const [stepDropdownOpen, setStepDropdownOpen] = useState(false);
  // { label, message?, run } — run is invoked on Confirm; null means modal closed.
  const [confirmAction, setConfirmAction] = useState(null);
  // Once a case-resolving action is confirmed, hide the remaining actions so the
  // user can't approve/reject/send twice. The backend status change isn't
  // reflected locally, so without this the buttons would stay clickable.
  const [actionSubmitted, setActionSubmitted] = useState(false);
  // True once the case is resolved via Approve/Reject in this session. Together
  // with the Closed/Rejected early-return below, this is the "case is terminal"
  // signal: while NOT terminal, both compliance and advisor may comment (even if
  // the other role currently holds the case), and the send-to-step control hides.
  const [caseResolved, setCaseResolved] = useState(false);
  // True once any status-changing action succeeds (send to advisor/compliance,
  // second approval, close, ROS, send-to-step, approve/reject). Used to hide the
  // send-to-role/step control: once the case has been moved/acted on, this user
  // shouldn't keep routing it. Does NOT gate Add comment (a handed-off case is
  // still open and both roles may comment).
  const [statusActionTaken, setStatusActionTaken] = useState(false);

  // Reusable so we can refresh after an action: the backend recomputes which
  // actions are allowed once the status changes, and that fresh result drives
  // button visibility. `done` runs once the refetch settles.
  const loadAllowedActions = React.useCallback(
    (done) => {
      if (alertId && fetchs && fetchs.getAlertAllowedActions) {
        fetchs.getAlertAllowedActions({ AlertId: alertId }, (response) => {
          if (response && response.AlertAllowedActions) {
            setAllowedActions(response.AlertAllowedActions);
          }
          setAllowedLoaded(true);
          if (typeof done === "function") done();
        });
      } else {
        setAllowedLoaded(true);
        if (typeof done === "function") done();
      }
    },
    // `fetchs` is an inline object recreated each render at the app level, so it
    // is intentionally omitted to keep this callback stable per alertId and avoid
    // a refetch loop. The functions inside it are stable module imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertId]
  );

  const requestConfirm = (label, run, message) => {
    setConfirmAction({ label, run, message });
  };

  const cancelConfirm = () => setConfirmAction(null);

  const executeConfirmed = () => {
    if (confirmAction && typeof confirmAction.run === "function") {
      confirmAction.run();
    }
    setConfirmAction(null);
    // All non-download actions funnel through here, so this is the single point
    // to lock the case once an action is taken.
    setActionSubmitted(true);
  };

  // Normalizes the assorted response shapes from updateStatus / confirmAlertChanges
  // / closeAccountAlert into a single success/error toast. The natural-language
  // copy takes precedence over the backend's Description field.
  const handleResponse = (messages, response, exception) => {
    const success = (messages && messages.success) || "Action completed.";
    const error = (messages && messages.error) || "An error occurred. Please try again.";

    if (exception) {
      // eslint-disable-next-line no-console
      console.error("[ChangesActions] error:", exception);
      showToast(error, true);
      // Action failed — release the optimistic lock so the user can retry.
      setActionSubmitted(false);
      return false;
    }

    const result = response && response.result;
    const code = result && (result.Code !== undefined ? result.Code : result.code);
    const desc = result && (result.Description || result.description);
    const ok = !code || code === 200 || code === "200";

    if (ok) {
      showToast(success, false);
      // Any successful status action means the case has been moved/acted on.
      setStatusActionTaken(true);
      // Refresh the case in the parent so the alert status updates. This is what
      // makes the gating survive a tab switch: the local flags reset when this
      // panel remounts, but the refreshed status/allowedActions/steps (all
      // refetched on mount) then drive visibility from real backend state.
      if (fetchs && fetchs.refreshAlert) fetchs.refreshAlert();
      // Refresh allowed actions from the backend; once the fresh result lands,
      // drop the optimistic lock so the real state drives button visibility.
      loadAllowedActions(() => setActionSubmitted(false));
    } else {
      showToast(desc || error, true);
      // Rejected by the backend — release the lock so the user can retry.
      setActionSubmitted(false);
    }
    return ok;
  };

  useEffect(() => {
    loadAllowedActions();
  }, [alertId, loadAllowedActions]);

  useEffect(() => {
    if (fetchs && fetchs.getParameterValue) {
      fetchs.getParameterValue({ ParameterName: "UpdateKYCStatus" }, (response) => {
        if (response && response.SdtParametersData) {
          setUpdateKYCStatus(response.SdtParametersData.ParameterNumericValue == 1);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (fetchs && fetchs.getEnabledAllAlertFeatures) {
      fetchs.getEnabledAllAlertFeatures({}, (response) => {
        if (response && response.enabledAlertFeatures) {
          setEnabledAlertFeatures(response.enabledAlertFeatures);
        }
        setFeaturesLoaded(true);
      });
    } else {
      setFeaturesLoaded(true);
    }
  }, []);

  // Fetch available steps only for RiskChange in the KYC sub-tab.
  useEffect(() => {
    if (!alertId || !fetchs || !fetchs.getAvailableAlertSteps) return;
    if (processes !== "RiskChange") return;

    setLoadingSteps(true);
    fetchs.getAvailableAlertSteps({ id: alertId }, (response) => {
      if (response && response.SDTAlertTransition) {
        const steps = response.SDTAlertTransition.map((step) => ({
          id: step.AlertStepTransitionId,
          name: step.AlertStepTransitionName,
          canChangeStep: step.canChangeStep,
        }));
        setAvailableSteps(steps);
      } else {
        setAvailableSteps([]);
      }
      setLoadingSteps(false);
    });
  }, [alertId, processes]);

  const closeAlert = () => {
    if (!fetchs || !fetchs.updateStatus) return;
    setClosing({ loading: true });
    fetchs.updateStatus({ eventId: alertId, statusCompliance: "Closed" }, (response, exception) => {
      setClosing(1);
      handleResponse(MESSAGES.closeAlert, response, exception);
    });
  };

  const alertToROS = (messages) => {
    if (!fetchs || !fetchs.updateStatus) return;
    setSending({ loading: true });
    fetchs.updateStatus({ eventId: alertId, statusCompliance: "ROS" }, (response, exception) => {
      setSending(1);
      handleResponse(messages || MESSAGES.markAsROS, response, exception);
    });
  };

  const closeAccount = () => {
    if (!fetchs || !fetchs.closeAccountAlert) return;
    setLoadingCloseAccount(true);
    fetchs.closeAccountAlert({ eventId: alertId, accountId: typeAlias }, (response, exception) => {
      setLoadingCloseAccount(false);
      const ok = handleResponse(MESSAGES.closeAccount, response, exception);
      if (ok) setShowMessageCloseAccount(true);
    });
  };

  const updateStatusWithToast = (messages, statusCompliance) => {
    if (!fetchs || !fetchs.updateStatus) return;
    fetchs.updateStatus(
      { eventId: alertId, statusCompliance },
      (response, exception) => handleResponse(messages, response, exception)
    );
  };

  const confirmChangesWithToast = (messages, action) => {
    if (!fetchs || !fetchs.confirmAlertChanges) return;
    fetchs.confirmAlertChanges(
      { ComplianceEventid: alertId, Action: action },
      (response, exception) => {
        const ok = handleResponse(messages, response, exception);
        // Approve/Reject are terminal — the case becomes closed/rejected.
        if (ok) {
          setCaseResolved(true);
          // Refrescar la persona del perfil: tras aprobar/rechazar, el backend
          // recalcula riesgo/límite transaccional y la card de arriba (que lee
          // del objeto `person`) debe mostrar los valores nuevos.
          if (typeof onResolved === "function") onResolved();
        }
      }
    );
  };

  const runSendToStep = () => {
    if (!fetchs || !fetchs.sendToStep || !selectedStep) return;
    setSendingStep(true);
    fetchs.sendToStep(
      { id: alertId, stepId: selectedStep.id },
      (response, exception) => {
        setSendingStep(false);
        const ok = handleResponse(MESSAGES.sendToStep, response, exception);
        if (ok) setSelectedStep(null);
      }
    );
  };

  const downloadPdf = () => {
    if (fetchs && fetchs.downloadPdf) fetchs.downloadPdf({ eventId: alertId });
  };

  if (!alertId) return null;
  // Closed and Rejected are terminal states — only Download PDF remains.
  if (status === "Closed" || status === "Rejected") {
    return (
      <div style={sectionCardStyle}>
        <div style={sectionTitleStyle}>Actions</div>
        <div style={actionsRowStyle}>
          <Button color={BTN_COLOR} size={BTN_SIZE} onClick={downloadPdf}>
            <i className="fa fa-download" /> Download PDF
          </Button>
        </div>
      </div>
    );
  }

  // Mirrors Summary.js: when this is true, only the "renderAllowedButtons"
  // block applies; legacy buttons are hidden. When false, legacy applies and
  // renderAllowedButtons is hidden.
  const isAlertTypeEnabled =
    !!(enabledAlertFeatures &&
      enabledAlertFeatures.AlertFlowEnabled &&
      enabledAlertFeatures.AlertType &&
      enabledAlertFeatures.AlertType.some(
        (t) => t.AlertTypeInternalName === processes && t.AlertTypeEnabled
      ));

  const showCloseAlertLegacy =
    !actionSubmitted &&
    !isAlertTypeEnabled &&
    ((status != "Closed" &&
      status != "ROS" &&
      closing != 1 &&
      processes != "RiskChanges" &&
      role == "Compliance") ||
      (processes == "ExternalImport" && status != "Closed") ||
      (role == "CEO" && processes == "RiskChanges" && status != "Closed"));

  const showKYCNotApprovedLegacy =
    !actionSubmitted &&
    !isAlertTypeEnabled &&
    status != "Closed" &&
    status != "ROS" &&
    sending != 1 &&
    processes == "KYC" &&
    role == "Compliance" &&
    updateKYCStatus;

  // In Summary, this is wrapped by:
  //   (processes == "TransactionalLimit" ||
  //    (processes == "KYC" && enabledAlertFeatures.AlertFlowEnabled))
  // combined with `processes !== "KYC"` inside. Net effect: only fires for
  // TransactionalLimit. We replicate that exactly.
  const showMarkAsROSLegacy =
    !actionSubmitted &&
    !isAlertTypeEnabled &&
    processes === "TransactionalLimit" &&
    status != "Closed" &&
    status != "ROS" &&
    sending != 1 &&
    role == "Compliance";

  // Download PDF is available for every KYC change request status.
  const showDownloadPdf = !!(fetchs && fetchs.downloadPdf);

  // Outer wrapper from Summary: renderAllowedButtons is only invoked when
  // processes is TransactionalLimit/RiskChange/KYC.
  const allowedActionsApplicable =
    isAlertTypeEnabled &&
    (processes === "TransactionalLimit" || processes === "RiskChange" || processes === "KYC");

  // SendStepDropdown — gated to RiskChange in the KYC sub-tab. Hidden once the
  // case is terminal (Closed/Rejected/approved-in-session) and if any step has
  // canChangeStep=false.
  const showSendStepDropdown =
    !actionSubmitted &&
    !caseResolved &&
    !statusActionTaken &&
    status !== "Rejected" &&
    status !== "Closed" &&
    processes === "RiskChange" &&
    availableSteps.length > 0 &&
    availableSteps.every((step) => step.canChangeStep === true);

  const hasAllowedButtons =
    allowedActions &&
    allowedActionsApplicable &&
    ((allowedActions.ROS && processes !== "KYC") ||
      (allowedActions.ROS && processes === "KYC") ||
      (allowedActions.Close && processes !== "RiskChange") ||
      (allowedActions.ToAdvisor &&
        (processes === "TransactionalLimit" || processes === "RiskChange" || processes === "KYC")) ||
      (allowedActions.ToSecondApproval &&
        (processes === "TransactionalLimit" || processes === "RiskChange")) ||
      (allowedActions.ToCompliance &&
        (processes === "TransactionalLimit" || processes === "RiskChange" || processes === "KYC")) ||
      (allowedActions.Close && processes === "RiskChange") ||
      (allowedActions.Rejected && processes === "RiskChange"));

  const hasAnyButton =
    showDownloadPdf || showCloseAlertLegacy || showKYCNotApprovedLegacy || showMarkAsROSLegacy || hasAllowedButtons;

  // The legacy vs allowed-actions split (and which allowed buttons) depends on
  // enabledAlertFeatures + allowedActions. Until both settle, render a spinner so
  // we don't flash the wrong set first (e.g. "Close alert" → "Approve changes").
  const actionsReady = featuresLoaded && allowedLoaded;

  return (
    <div style={sectionCardStyle}>
      <div style={sectionTitleStyle}>Actions</div>

      {actionSubmitted && (
        <div style={{ color: "#90a4ae", fontSize: 13, marginBottom: 8 }}>
          Processing action…
        </div>
      )}

      {!actionsReady ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#90a4ae", fontSize: 13 }}>
          <CircularProgress sm style={{ margin: 0 }} /> Loading actions…
        </div>
      ) : (
      <React.Fragment>
      {!hasAnyButton && (
        <div style={{ color: "#90a4ae", fontSize: 13, marginBottom: 8 }}>
          No actions available for this case.
        </div>
      )}

      <div style={actionsRowStyle}>
        {showDownloadPdf && (
          <Button color={BTN_COLOR} size={BTN_SIZE} onClick={downloadPdf}>
            <i className="fa fa-download" /> Download PDF
          </Button>
        )}

        {showCloseAlertLegacy && (
          <LaddaButton
            className={BTN_CLASS}
            loading={closing.loading}
            onClick={() => requestConfirm("Close alert", closeAlert, MESSAGES.closeAlert.confirm)}
            data-style={SLIDE_DOWN}
          >
            Close alert
          </LaddaButton>
        )}

        {showKYCNotApprovedLegacy && (
          <LaddaButton
            className={BTN_CLASS}
            loading={sending.loading}
            onClick={() =>
              requestConfirm(
                "KYC not approved",
                () => alertToROS(MESSAGES.kycNotApproved),
                MESSAGES.kycNotApproved.confirm
              )
            }
            data-style={SLIDE_LEFT}
          >
            KYC not approved
          </LaddaButton>
        )}

        {showMarkAsROSLegacy && (
          <LaddaButton
            className={BTN_CLASS}
            loading={sending.loading}
            onClick={() =>
              requestConfirm(
                "Mark as ROS",
                () => alertToROS(MESSAGES.markAsROS),
                MESSAGES.markAsROS.confirm
              )
            }
            data-style={SLIDE_LEFT}
          >
            Mark as ROS
          </LaddaButton>
        )}

        {!actionSubmitted && allowedActions && allowedActionsApplicable && (
          <React.Fragment>
            {allowedActions.ROS && processes !== "KYC" && (
              <LaddaButton
                className={BTN_CLASS}
                loading={sending.loading}
                onClick={() =>
                  requestConfirm(
                    "Mark as ROS",
                    () => alertToROS(MESSAGES.markAsROS),
                    MESSAGES.markAsROS.confirm
                  )
                }
                data-style={SLIDE_LEFT}
              >
                Mark as ROS
              </LaddaButton>
            )}
            {allowedActions.ROS && processes === "KYC" && (
              <LaddaButton
                className={BTN_CLASS}
                loading={sending.loading}
                onClick={() =>
                  requestConfirm(
                    "KYC not approved",
                    () => alertToROS(MESSAGES.kycNotApproved),
                    MESSAGES.kycNotApproved.confirm
                  )
                }
                data-style={SLIDE_LEFT}
              >
                KYC not approved
              </LaddaButton>
            )}
            {allowedActions.Close && processes !== "RiskChange" && (
              <LaddaButton
                className={BTN_CLASS}
                loading={closing.loading}
                onClick={() => requestConfirm("Close alert", closeAlert, MESSAGES.closeAlert.confirm)}
                data-style={SLIDE_DOWN}
              >
                Close alert
              </LaddaButton>
            )}
            {allowedActions.ToAdvisor &&
              (processes === "TransactionalLimit" ||
                processes === "RiskChange" ||
                processes === "KYC") && (
                <Button
                  color={BTN_COLOR}
                  size={BTN_SIZE}
                  onClick={() =>
                    requestConfirm(
                      "Send to Advisor",
                      () => updateStatusWithToast(MESSAGES.sendToAdvisor, "WorkingAdvisor"),
                      MESSAGES.sendToAdvisor.confirm
                    )
                  }
                >
                  Send to Advisor
                </Button>
              )}
            {allowedActions.ToSecondApproval &&
              (processes === "TransactionalLimit" || processes === "RiskChange") && (
                <Button
                  color={BTN_COLOR}
                  size={BTN_SIZE}
                  onClick={() =>
                    requestConfirm(
                      "Second Approval",
                      () => updateStatusWithToast(MESSAGES.secondApproval, "WorkingCEO"),
                      MESSAGES.secondApproval.confirm
                    )
                  }
                >
                  Second Approval
                </Button>
              )}
            {allowedActions.ToCompliance &&
              (processes === "TransactionalLimit" ||
                processes === "RiskChange" ||
                processes === "KYC") && (
                <Button
                  color={BTN_COLOR}
                  size={BTN_SIZE}
                  onClick={() =>
                    requestConfirm(
                      "Send To Compliance",
                      () => updateStatusWithToast(MESSAGES.sendToCompliance, "WorkingCompliance"),
                      MESSAGES.sendToCompliance.confirm
                    )
                  }
                >
                  Send To Compliance
                </Button>
              )}
            {allowedActions.Close && processes === "RiskChange" && (
              <Button
                color={BTN_COLOR}
                size={BTN_SIZE}
                onClick={() =>
                  requestConfirm(
                    "Approve changes",
                    () => confirmChangesWithToast(MESSAGES.approve, "Approve"),
                    MESSAGES.approve.confirm
                  )
                }
              >
                Approve changes
              </Button>
            )}
            {allowedActions.Rejected && processes === "RiskChange" && (
              <Button
                color={BTN_COLOR}
                size={BTN_SIZE}
                onClick={() =>
                  requestConfirm(
                    "Reject changes",
                    () => confirmChangesWithToast(MESSAGES.reject, "Reject"),
                    MESSAGES.reject.confirm
                  )
                }
              >
                Reject changes
              </Button>
            )}
          </React.Fragment>
        )}

        {!caseResolved && fetchs && fetchs.addToHistory && (
          <Button
            color={BTN_COLOR}
            size={BTN_SIZE}
            outline
            onClick={() => setShowCommentForm((v) => !v)}
          >
            <i className="fa fa-comment-o" aria-hidden="true" />{" "}
            {showCommentForm ? "Hide comment" : "Add comment"}
          </Button>
        )}
      </div>
      </React.Fragment>
      )}

      {!caseResolved && showCommentForm && fetchs && fetchs.addToHistory && (
        <CommentForm
          alertId={alertId}
          fetchs={fetchs}
          currentUser={currentUser}
          onSubmitted={() => setShowCommentForm(false)}
          onCancel={() => setShowCommentForm(false)}
        />
      )}

      {showSendStepDropdown && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            background: "#f8f9fb",
            border: "1px solid #e3e8ec",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <strong style={{ fontSize: 13, color: "#455a64" }}>Send to role/step:</strong>
          {loadingSteps ? (
            <span style={{ color: "#90a4ae", fontSize: 12 }}>Loading...</span>
          ) : (
            <React.Fragment>
              <Dropdown
                isOpen={stepDropdownOpen}
                toggle={() => setStepDropdownOpen((o) => !o)}
              >
                <DropdownToggle caret size="sm" color="light" style={{ minWidth: 180 }}>
                  {selectedStep ? selectedStep.name : "Select step"}
                </DropdownToggle>
                <DropdownMenu>
                  {availableSteps.map((step) => (
                    <DropdownItem
                      key={step.id}
                      onClick={() => {
                        setSelectedStep(step);
                        setStepDropdownOpen(false);
                      }}
                    >
                      {step.name}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
              {selectedStep && selectedStep.canChangeStep && (
                <Button
                  color={BTN_COLOR}
                  size={BTN_SIZE}
                  disabled={sendingStep}
                  onClick={() =>
                    requestConfirm(
                      "Send to step",
                      runSendToStep,
                      MESSAGES.sendToStep.confirm
                    )
                  }
                >
                  Send
                </Button>
              )}
            </React.Fragment>
          )}
        </div>
      )}

      {requestClosure && (
        <div style={{ marginTop: 14 }}>
          {closedAccount ? (
            <div style={{ color: "mediumseagreen" }}>
              The account {name} is Closed
              {closedAccountDate ? ` — Date of closure: ${closedAccountDate}` : ""}
            </div>
          ) : showMessageCloseAccount ? (
            <div style={{ color: "mediumseagreen" }}>The account was successfully closed</div>
          ) : !actionSubmitted ? (
            <div>
              <div style={{ marginBottom: 6 }}>
                <strong>Close Account</strong>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span>Do you want to Close Account {name}?</span>
                <LaddaButton
                  className={BTN_CLASS}
                  loading={loadingCloseAccount}
                  disabled={false}
                  onClick={() =>
                    requestConfirm("Close account", closeAccount, MESSAGES.closeAccount.confirm)
                  }
                  data-style={SLIDE_LEFT}
                >
                  Confirm
                </LaddaButton>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <Modal isOpen={!!confirmAction} toggle={cancelConfirm} centered>
        <ModalHeader toggle={cancelConfirm}>Confirm action</ModalHeader>
        <ModalBody>
          {confirmAction && confirmAction.message
            ? confirmAction.message
            : `Are you sure you want to ${confirmAction ? confirmAction.label.toLowerCase() : "perform this action"}?`}
        </ModalBody>
        <ModalFooter>
          <Button color="light" size="sm" onClick={cancelConfirm}>
            Cancel
          </Button>
          <Button color="primary" size="sm" onClick={executeConfirmed}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
